import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  IdentificationIcon,
  MapPinIcon,
  AtSymbolIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import Layout from '../components/Layout';
import ChangeEmailModal from '../components/ChangeEmailModal';
import { usersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [emailChangeError, setEmailChangeError] = useState('');

  // Fetch user details
  const { data: userData, isLoading } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => usersAPI.getUserById(userId),
  });

  // Activate mutation
  const activateMutation = useMutation({
    mutationFn: () => usersAPI.activateUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries(['user', userId]);
      toast.success('Usuario activado exitosamente');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error al activar usuario');
    },
  });

  // Deactivate mutation
  const deactivateMutation = useMutation({
    mutationFn: () => usersAPI.deactivateUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries(['user', userId]);
      toast.success('Usuario desactivado exitosamente');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error al desactivar usuario');
    },
  });

  // Change email mutation
  const changeEmailMutation = useMutation({
    mutationFn: (newEmail) => usersAPI.changeUserEmail(userId, newEmail),
    onSuccess: () => {
      queryClient.invalidateQueries(['user', userId]);
      toast.success('Email cambiado exitosamente');
      setShowChangeEmailModal(false);
      setEmailChangeError('');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || 'Error al cambiar email';
      setEmailChangeError(errorMessage);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => usersAPI.deleteUser(userId),
    onSuccess: () => {
      toast.success('Usuario eliminado exitosamente');
      navigate('/users');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error al eliminar usuario');
    },
  });

  const handleDelete = () => {
    if (window.confirm(`¿Estás seguro que quieres eliminar a ${user.displayName}? Esta acción no se puede deshacer.`)) {
      deleteMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const user = userData?.data;

  if (!user) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">Usuario no encontrado</p>
          <Link
            to="/users"
            className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-500"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Volver a Usuarios
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Link
              to="/users"
              className="mr-4 text-gray-400 hover:text-gray-500"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {user.displayName}
              </h1>
              <p className="mt-1 text-sm text-gray-500">User Details</p>
            </div>
          </div>
          <div className="flex gap-2">
            {hasPermission('user:update') && (
              <Link
                to={`/users/${userId}/edit`}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit
              </Link>
            )}
            {hasPermission('user:activate') && (
              <>
                {user.accountEnabled ? (
                  <button
                    onClick={() => deactivateMutation.mutate()}
                    disabled={deactivateMutation.isLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                  >
                    <XCircleIcon className="h-4 w-4 mr-2" />
                    Deactivate
                  </button>
                ) : (
                  <button
                    onClick={() => activateMutation.mutate()}
                    disabled={activateMutation.isLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Activate
                  </button>
                )}
              </>
            )}
            {hasPermission('user:delete') && (
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-700 hover:bg-red-800 disabled:opacity-50"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                Delete
              </button>
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div>
          {user.accountEnabled ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <CheckCircleIcon className="w-5 h-5 mr-1" />
              Active Account
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
              <XCircleIcon className="w-5 h-5 mr-1" />
              Inactive Account
            </span>
          )}
        </div>

        {/* User Information */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Personal Information
            </h3>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Full Name</dt>
                <dd className="mt-1 text-sm text-gray-900">{user.displayName}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center justify-between">
                  <span className="flex items-center">
                    <EnvelopeIcon className="h-4 w-4 mr-1" />
                    Email
                  </span>
                  {hasPermission('user:update') && (
                    <button
                      onClick={() => {
                        setEmailChangeError('');
                        setShowChangeEmailModal(true);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-500 flex items-center"
                      title="Cambiar email"
                    >
                      <AtSymbolIcon className="h-3 w-3 mr-1" />
                      Cambiar
                    </button>
                  )}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{user.email || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <IdentificationIcon className="h-4 w-4 mr-1" />
                  Document Type
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{user.documentType || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Document Number</dt>
                <dd className="mt-1 text-sm text-gray-900">{user.documentNumber || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <PhoneIcon className="h-4 w-4 mr-1" />
                  Mobile Phone
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{user.businessPhones?.[0] || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">User ID</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono text-xs">{user.id}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Address Information */}
        {(user.streetAddress || user.city || user.state || user.postalCode || user.country) && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <MapPinIcon className="h-5 w-5 mr-2" />
                Address
              </h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Street Address</dt>
                  <dd className="mt-1 text-sm text-gray-900">{user.streetAddress || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">City</dt>
                  <dd className="mt-1 text-sm text-gray-900">{user.city || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">State</dt>
                  <dd className="mt-1 text-sm text-gray-900">{user.state || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Postal Code</dt>
                  <dd className="mt-1 text-sm text-gray-900">{user.postalCode || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Country</dt>
                  <dd className="mt-1 text-sm text-gray-900">{user.country || '-'}</dd>
                </div>
              </dl>
            </div>
          </div>
        )}

        {/* Timestamps */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Timestamps
            </h3>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Created At</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(user.createdDateTime).toLocaleString()}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Change Email Modal */}
        <ChangeEmailModal
          isOpen={showChangeEmailModal}
          onClose={() => {
            setShowChangeEmailModal(false);
            setEmailChangeError('');
          }}
          currentEmail={user.email}
          onSubmit={(newEmail) => changeEmailMutation.mutate(newEmail)}
          isLoading={changeEmailMutation.isLoading}
          serverError={emailChangeError}
        />
      </div>
    </Layout>
  );
};

export default UserDetail;
